import type { ICampaignTableHeading } from '../types';

import { GTextLink, GTooltip, GTooltipCard, GStopPropagation, GTextField } from '~/presentation/ui';
import { Box, Button, InlineGrid, InlineStack, Text } from '@shopify/polaris';
import { useGCampaignTableContext } from '../contexts';
import { GCampaignTable } from '../GCampaignTable';
import { EditIcon } from '@shopify/polaris-icons';
import { useToggle } from '~/hooks/useToggle';
import { useState } from 'react';

export const Header = () => {
  const [newTitle, setNewTitle] = useState('');

  const { listHeader, alignments, parsePaddingRow } = useGCampaignTableContext();
  const { setFalse, setTrue, value } = useToggle(false);
  const { setFalse: setOffRename, setTrue: setOnRenaming, value: isRenaming } = useToggle(false);

  const onRename = (title: string) => {
    setNewTitle(title);
    setOnRenaming();
  };
  const handleRename = async (header: ICampaignTableHeading<string>) => {
    setOffRename();
    await header.rename?.onAction?.(newTitle.trim());
  };

  const renderHeaderContent = (header: ICampaignTableHeading<string>, index: number) => {
    const headingAlignment = alignments[index] ?? 'start';

    const headingTitle = (
      <div className="overflow-hidden">
        <GTextLink linkAction={header.linkAction} isDisabled={header.isStopPropagation}>
          <GTooltip
            content={header.title}
            hoverDelay={500}
            width="wide"
            isEnabled={!!header.text?.isEnabledTooltip}
          >
            <Text alignment={headingAlignment} variant="bodySm" as="p" {...header.text}>
              {header.title}
            </Text>
          </GTooltip>
        </GTextLink>
      </div>
    );

    const headingTitleRename = (
      <div className="overflow-hidden">
        <InlineGrid
          columns="100%"
          {...((value || header.rename?.loading) && { columns: 'calc(100% - 36px) 28px' })}
          gap={'200'}
          alignItems="center"
        >
          <>
            <GTooltip
              content={header.title}
              width="wide"
              isEnabled={!!header.text?.isEnabledTooltip}
            >
              <Text alignment={headingAlignment} variant="bodySm" as="p" {...header.text}>
                {header.title}
              </Text>
            </GTooltip>
            {(value || header.rename?.loading) && (
              <GTooltip content={'Rename'} isEnabled={true}>
                <div className="w-[28px] h-[28px] flex items-center justify-center">
                  <Button
                    size="slim"
                    loading={header.rename?.loading}
                    variant="tertiary"
                    icon={EditIcon}
                    onClick={() => onRename(header.title as string)}
                  ></Button>
                </div>
              </GTooltip>
            )}
          </>
        </InlineGrid>
      </div>
    );

    const HeaderContent = header.tooltipCard ? (
      <>
        <GTooltipCard tooltip={header.tooltipCard}>
          <Text alignment={headingAlignment} variant="bodySm" as="p" {...header.text}>
            {header.title}
          </Text>
        </GTooltipCard>
      </>
    ) : (
      <InlineStack align={headingAlignment} blockAlign="center" gap="200" wrap={false}>
        {header.rename ? headingTitleRename : headingTitle}
      </InlineStack>
    );

    return (
      <Box maxWidth="100%" width="100%">
        {HeaderContent}
      </Box>
    );
  };

  const renderHeader = (header: ICampaignTableHeading<string>, index: number, id?: string) => {
    const paddingBox = parsePaddingRow(index);
    const isHighlightConner = header.highlight && header.highlightConner;

    if (header.isFlushLeft) return;
    return (
      <GStopPropagation key={id} isDisabled={!header.isStopPropagation} height={'100%'}>
        <div className="h-full" onMouseEnter={setTrue} onMouseLeave={setFalse}>
          {isRenaming && header.rename ? (
            <Box {...paddingBox}>
              <div className="w-full pl-1">
                <GTextField
                  size="slim"
                  focused={true}
                  maxLength={header.rename?.maxLength || 255}
                  error={header.rename?.error || ''}
                  placeholder={header.rename?.placeholder || ''}
                  value={newTitle}
                  onChange={(value) => setNewTitle(value)}
                  onBlur={() => handleRename(header)}
                  onEnter={() => handleRename(header)}
                />
              </div>
            </Box>
          ) : (
            <GCampaignTable.Cell
              key={`${index}-cell`}
              index={index}
              padding={paddingBox}
              minHeight={header.height}
              {...(isHighlightConner && {
                borders: {
                  borderStartStartRadius: '200',
                  borderStartEndRadius: '200',
                },
              })}
            >
              <div id={id} className="max-w-full h-full flex items-center">
                {renderHeaderContent(header, index)}
              </div>
            </GCampaignTable.Cell>
          )}
        </div>
      </GStopPropagation>
    );
  };

  return listHeader.map((header, index) => renderHeader(header, index, header.id));
};
